import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import swal from "sweetalert";
import { db, functions } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useRecoveryDeleteAccess } from "../../../hook/useRecoveryDeleteAccess";
import PageTitle from "../../layouts/PageTitle";
import {
  filterRecoveryRecordsByHn,
  getCpapTrialRecoveryHn,
  RECOVERY_HN_FILTER_ALL,
  RECOVERY_HN_FILTER_EMPTY,
  RECOVERY_HN_FILTER_WITH_HN,
} from "../RecoveryHnUtils";
import Toast, { notifyError, notifyTopRight } from "../toast/SetToast";

const BULK_EMPTY_HN_WORKING_ID = "bulk-empty-hn";

const formatDateTime = (value) => {
  if (!value) return "-";

  let date = value;
  if (value && typeof value.toDate === "function") {
    date = value.toDate();
  } else if (
    value &&
    typeof value === "object" &&
    typeof value.seconds === "number"
  ) {
    date = new Date(value.seconds * 1000);
  } else {
    date = new Date(value);
  }

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTrialProps = (record) =>
  (record.trial && record.trial.extendedProps) || {};

const getSummaryValue = (record, field) => {
  const summary = record.summary || {};
  const trialProps = getTrialProps(record);
  const fallbackValues = {
    order: trialProps.order,
    hn: trialProps.HN || trialProps.hn,
    patientName: [
      trialProps.FirstName || trialProps.firstname,
      trialProps.LastName || trialProps.lastname,
    ]
      .filter(Boolean)
      .join(" "),
    node: trialProps.nodeRef || trialProps.node,
    status: trialProps.Status || trialProps.status,
    serialNumber: trialProps.SerialNumber,
    activity: trialProps.Activity,
  };

  if (field === "hn") return getCpapTrialRecoveryHn(record) || "-";

  return summary[field] || fallbackValues[field] || "-";
};

const getRecordText = (record) => {
  const trialProps = getTrialProps(record);

  return [
    getSummaryValue(record, "order"),
    getSummaryValue(record, "hn"),
    getSummaryValue(record, "patientName"),
    getSummaryValue(record, "node"),
    getSummaryValue(record, "serialNumber"),
    trialProps.order,
    trialProps.HN,
    trialProps.FirstName,
    trialProps.LastName,
    record.deletedBy && record.deletedBy.displayName,
    record.deletedBy && record.deletedBy.email,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const CpapTrialRecovery = () => {
  const { currentUser } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [hnFilter, setHnFilter] = useState(RECOVERY_HN_FILTER_WITH_HN);
  const [emptyHnCount, setEmptyHnCount] = useState(null);
  const [emptyHnCountLoading, setEmptyHnCountLoading] = useState(true);
  const { hasRecoveryDeleteAccess } = useRecoveryDeleteAccess();

  const restoreDeletedCpapTrial = httpsCallable(
    functions,
    "restoreDeletedCpapTrial",
  );
  const permanentlyDeleteDeletedCpapTrial = httpsCallable(
    functions,
    "permanentlyDeleteDeletedCpapTrial",
  );
  const permanentlyDeleteEmptyHnCpapTrialRecoveryRecords = httpsCallable(
    functions,
    "permanentlyDeleteEmptyHnCpapTrialRecoveryRecords",
  );
  const currentRoles =
    currentUser && Array.isArray(currentUser.roles) ? currentUser.roles : [];
  const isSuperAdmin = currentRoles.includes("super_admin");

  const loadRecoveryRecords = useCallback(async () => {
    setLoading(true);
    if (isSuperAdmin) setEmptyHnCountLoading(true);
    try {
      const recoveryQuery = query(
        collection(db, "deletedCpapTrials"),
        orderBy("deletedAt", "desc"),
        limit(250),
      );
      const snapshot = await getDocs(recoveryQuery);
      setRecords(
        snapshot.docs.map((record) => ({ id: record.id, ...record.data() })),
      );
    } catch (error) {
      console.error("Unable to load CPAP Trial recovery records", error);
      notifyError(
        `Unable to load CPAP Trial Recovery: ${(error && error.message) || error}`,
      );
    } finally {
      setLoading(false);
    }

    if (!isSuperAdmin) {
      setEmptyHnCount(null);
      setEmptyHnCountLoading(false);
      return;
    }

    try {
      const getEmptyHnCpapTrialRecoveryRecordCount = httpsCallable(
        functions,
        "getEmptyHnCpapTrialRecoveryRecordCount",
      );
      const response = await getEmptyHnCpapTrialRecoveryRecordCount();
      setEmptyHnCount(
        Number(response && response.data && response.data.emptyHnCount) || 0,
      );
    } catch (error) {
      console.error("Unable to load empty-HN CPAP Trial recovery count", error);
      setEmptyHnCount(null);
      notifyError(
        `Unable to load empty-HN total: ${(error && error.message) || error}`,
      );
    } finally {
      setEmptyHnCountLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadRecoveryRecords();
  }, [loadRecoveryRecords]);

  const visibleRecords = useMemo(() => {
    const hnFilteredRecords = filterRecoveryRecordsByHn(
      records,
      hnFilter,
      getCpapTrialRecoveryHn,
    );
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) return hnFilteredRecords;

    return hnFilteredRecords.filter((record) =>
      getRecordText(record).includes(normalizedSearch),
    );
  }, [hnFilter, records, searchText]);

  const restoreTrial = async (record) => {
    if (record.status !== "deleted" || workingId) return;

    const order = getSummaryValue(record, "order");
    const confirmed = await swal({
      title: `Restore CPAP Trial ${order}?`,
      text:
        "This restores the exact backed-up schedule to the CPAP Trial Calendar. An existing trial schedule will never be overwritten.",
      icon: "warning",
      buttons: ["Cancel", "Restore CPAP Trial"],
    });
    if (!confirmed) return;

    setWorkingId(record.id);
    try {
      await restoreDeletedCpapTrial({ recoveryId: record.id });
      notifyTopRight("CPAP Trial schedule restored to its calendar");
      await loadRecoveryRecords();
    } catch (error) {
      console.error("Unable to restore CPAP Trial schedule", error);
      notifyError(
        `Restore failed: ${(error && error.message) || error}`,
      );
    } finally {
      setWorkingId("");
    }
  };

  const permanentlyDeleteTrial = async (record) => {
    if (
      !hasRecoveryDeleteAccess ||
      record.status !== "deleted" ||
      workingId
    ) {
      return;
    }

    const order = getSummaryValue(record, "order");
    const confirmed = await swal({
      title: `Permanently delete CPAP Trial backup for ${order}?`,
      text:
        "This destroys the recovery copy and its audit log forever. It cannot be restored. Only an account approved in Recovery Delete Access can complete this action.",
      icon: "warning",
      buttons: ["Cancel", "Delete forever"],
      dangerMode: true,
    });
    if (!confirmed) return;

    setWorkingId(record.id);
    try {
      await permanentlyDeleteDeletedCpapTrial({ recoveryId: record.id });
      notifyTopRight("CPAP Trial recovery backup permanently deleted");
      await loadRecoveryRecords();
    } catch (error) {
      console.error("Unable to permanently delete CPAP Trial backup", error);
      notifyError(
        `Permanent delete failed: ${(error && error.message) || error}`,
      );
    } finally {
      setWorkingId("");
    }
  };

  const permanentlyDeleteEmptyHnRecords = async () => {
    if (!isSuperAdmin || !hasRecoveryDeleteAccess || workingId) return;

    const confirmed = await swal({
      title: "Permanently delete all empty-HN CPAP Trial records?",
      text:
        `This permanently deletes all ${
          Number.isFinite(emptyHnCount) ? emptyHnCount : "currently matching"
        } CPAP Trial Recovery records with no HN, including recovery copies and audit logs older than the 250 records shown here. This cannot be undone. Records with an HN are not affected.`,
      icon: "warning",
      buttons: ["Cancel", "Delete all empty-HN records"],
      dangerMode: true,
    });
    if (!confirmed) return;

    setWorkingId(BULK_EMPTY_HN_WORKING_ID);
    try {
      const response = await permanentlyDeleteEmptyHnCpapTrialRecoveryRecords();
      const permanentlyDeletedCount = Number(
        response &&
          response.data &&
          response.data.permanentlyDeletedCount,
      );

      setEmptyHnCount(0);

      notifyTopRight(
        permanentlyDeletedCount
          ? `${permanentlyDeletedCount} empty-HN CPAP Trial recovery record${
              permanentlyDeletedCount === 1 ? "" : "s"
            } permanently deleted`
          : "No empty-HN CPAP Trial recovery records were found",
      );
      await loadRecoveryRecords();
    } catch (error) {
      console.error(
        "Unable to permanently delete empty-HN CPAP Trial recovery records",
        error,
      );
      notifyError(
        `Bulk permanent delete failed: ${(error && error.message) || error}`,
      );
    } finally {
      setWorkingId("");
    }
  };

  return (
    <>
      <Toast />
      <PageTitle
        activeMenu="Recovery & Delete Log"
        motherMenu="CPAP Trial"
        motherLink="/trial-calendar"
      />

      <Card>
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center">
          <div>
            <h4 className="card-title mb-1">Recovery & Delete Log</h4>
            <small className="text-muted">
              Deleted CPAP Trial schedules stay recoverable here with the
              operator and time recorded. This is separate from Sleep Study
              Booking Recovery.
            </small>
            {isSuperAdmin ? (
              <div className="mt-2">
                <Badge
                  variant={
                    emptyHnCountLoading || !Number.isFinite(emptyHnCount)
                      ? "secondary"
                      : emptyHnCount > 0
                        ? "danger"
                        : "success"
                  }
                  className="px-2 py-1"
                >
                  Empty HN recovery cases:{" "}
                  {emptyHnCountLoading
                    ? "Loading…"
                    : Number.isFinite(emptyHnCount)
                      ? emptyHnCount
                      : "Unavailable"}
                </Badge>
              </div>
            ) : null}
          </div>
          <div className="d-flex mt-2 mt-md-0">
            {isSuperAdmin && hasRecoveryDeleteAccess ? (
              <Button
                variant="danger"
                size="sm"
                className="mr-2"
                onClick={permanentlyDeleteEmptyHnRecords}
                disabled={
                  loading ||
                  emptyHnCountLoading ||
                  !emptyHnCount ||
                  Boolean(workingId)
                }
              >
                {workingId === BULK_EMPTY_HN_WORKING_ID
                  ? "Deleting empty-HN cases…"
                  : "Delete all empty-HN cases"}
              </Button>
            ) : null}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={loadRecoveryRecords}
              disabled={
                loading || emptyHnCountLoading || Boolean(workingId)
              }
            >
              Refresh
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={5} lg={5}>
              <Form.Control
                type="search"
                placeholder="Search order, HN, patient, node, CPAP serial, or deleting user"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
            </Col>
            <Col md={4} lg={3} className="mt-2 mt-md-0">
              <Form.Control
                as="select"
                value={hnFilter}
                onChange={(event) => setHnFilter(event.target.value)}
                aria-label="Filter CPAP Trial recovery records by HN"
              >
                <option value={RECOVERY_HN_FILTER_WITH_HN}>
                  Hide empty HN cases
                </option>
                <option value={RECOVERY_HN_FILTER_EMPTY}>
                  Show empty HN cases only
                </option>
                <option value={RECOVERY_HN_FILTER_ALL}>Show all cases</option>
              </Form.Control>
            </Col>
            <Col className="text-md-right mt-2 mt-md-0 text-muted">
              {visibleRecords.length} record
              {visibleRecords.length === 1 ? "" : "s"}
            </Col>
          </Row>

          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>Deleted at</th>
                  <th>Order / HN</th>
                  <th>Patient / CPAP</th>
                  <th>Trial status</th>
                  <th>Deleted by</th>
                  <th>Recovery status</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      Loading CPAP Trial recovery records…
                    </td>
                  </tr>
                ) : visibleRecords.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      No deleted CPAP Trial schedules found.
                    </td>
                  </tr>
                ) : (
                  visibleRecords.map((record) => {
                    const deletedBy = record.deletedBy || {};
                    const isDeleted = record.status === "deleted";
                    const isWorking = workingId === record.id;

                    return (
                      <tr key={record.id}>
                        <td>{formatDateTime(record.deletedAt)}</td>
                        <td>
                          <strong>{getSummaryValue(record, "order")}</strong>
                          <br />
                          <small className="text-muted">
                            HN: {getSummaryValue(record, "hn")}
                          </small>
                        </td>
                        <td>
                          {getSummaryValue(record, "patientName")}
                          <br />
                          <small className="text-muted">
                            {getSummaryValue(record, "node")} · CPAP: {getSummaryValue(record, "serialNumber")}
                          </small>
                        </td>
                        <td>
                          {getSummaryValue(record, "status")}
                          <br />
                          <small className="text-muted">
                            {getSummaryValue(record, "activity")}
                          </small>
                        </td>
                        <td>
                          {deletedBy.displayName || "Unknown"}
                          {deletedBy.email ? (
                            <>
                              <br />
                              <small className="text-muted">
                                {deletedBy.email}
                              </small>
                            </>
                          ) : null}
                        </td>
                        <td>
                          <Badge variant={isDeleted ? "warning" : "success"}>
                            {isDeleted ? "Deleted / recoverable" : "Restored"}
                          </Badge>
                          {record.deleteMethod === "safety_net_untracked_delete" ? (
                            <>
                              <br />
                              <small className="text-danger">
                                Captured outside the app; actor unavailable
                              </small>
                            </>
                          ) : null}
                        </td>
                        <td className="text-right text-nowrap">
                          {isDeleted ? (
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => restoreTrial(record)}
                              disabled={Boolean(workingId)}
                            >
                              {isWorking ? "Working…" : "Restore"}
                            </Button>
                          ) : (
                            <span className="text-muted">Recovered</span>
                          )}
                          {isDeleted && hasRecoveryDeleteAccess ? (
                            <Button
                              size="sm"
                              variant="danger"
                              className="ml-2"
                              onClick={() => permanentlyDeleteTrial(record)}
                              disabled={Boolean(workingId)}
                            >
                              Delete forever
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default CpapTrialRecovery;
